import mammoth from "mammoth";

export const SECCIONES_SITE_SURVEY = [
  { key: "referencia", titulo: "Referencia del Cliente", columnas: 4, estilo: "filas" },
  { key: "objetivo", titulo: "Objetivo del Estudio de Site Survey", tipo: "lista" },
  { key: "bom", titulo: "BOM", columnas: 2, estilo: "filas" },
  { key: "equipo", titulo: "Equipo Participante", columnas: 3, estilo: "encabezado" },
  { key: "cronograma", titulo: "Cronograma a Alto Nivel", columnas: 8, estilo: "encabezado" },
  { key: "entrega", titulo: "Entrega y Aceptación", columnas: 1, estilo: "filas" },
];

const MAX_LOGOS = 2;
const CLAVES_CON_TABLA = ["referencia", "bom", "equipo", "cronograma", "entrega"];

const REGEX_DIACRITICOS = new RegExp(
  "[" + String.fromCharCode(0x0300) + "-" + String.fromCharCode(0x036f) + "]",
  "g"
);
const REGEX_NUMERACION = /^\s*\d+\.\s*/;
const REGEX_FECHA = /\d{1,2}\s+de\s+\w+\s+de\s+\d{4}/i;

const normalizar = (texto) =>
  texto
    .replace(REGEX_NUMERACION, "")
    .normalize("NFD")
    .replace(REGEX_DIACRITICOS, "")
    .toLowerCase()
    .trim();

export const base64ADatos = (base64) => {
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) {
    bytes[i] = binario.charCodeAt(i);
  }
  return bytes;
};

const buscarSeccion = (texto) => {
  const normalizado = normalizar(texto);
  return SECCIONES_SITE_SURVEY.find((s) => normalizado.includes(normalizar(s.titulo)));
};

const textoCelda = (celda) => {
  const parrafos = Array.from(celda.querySelectorAll("p"))
    .map((p) => p.textContent.trim())
    .filter(Boolean);
  return parrafos.length > 0 ? parrafos.join("\n") : celda.textContent.trim();
};

const extraerFilasTabla = (tabla, columnas) =>
  Array.from(tabla.querySelectorAll("tr"))
    .map((fila) => Array.from(fila.querySelectorAll("td,th")).map(textoCelda))
    .filter((fila) => fila.some((celda) => celda.length > 0))
    .map((fila) => {
      const ajustada = fila.slice(0, columnas);
      while (ajustada.length < columnas) ajustada.push("");
      return ajustada;
    });

const filaAObjetoColumnas = (fila) =>
  fila.reduce((acc, valor, indice) => ({ ...acc, [`col${indice + 1}`]: valor }), {});

const procesarListaRecursiva = (ul, profundidad, items) => {
  Array.from(ul.children).forEach((li) => {
    if (li.tagName.toLowerCase() !== "li") return;
    const textoPropio = Array.from(li.childNodes)
      .filter((n) => !(n.nodeType === 1 && ["ul", "ol"].includes(n.tagName.toLowerCase())))
      .map((n) => n.textContent)
      .join("")
      .trim();
    if (textoPropio) {
      items.push({ texto: (profundidad > 0 ? "   ○ " : "• ") + textoPropio });
    }
    Array.from(li.children)
      .filter((c) => ["ul", "ol"].includes(c.tagName.toLowerCase()))
      .forEach((subLista) => procesarListaRecursiva(subLista, profundidad + 1, items));
  });
};

const extraerNombreProyecto = (documento) => {
  const primerH1 = documento.body.querySelector("h1");
  const nodos = Array.from(documento.body.childNodes).filter((n) => n.nodeType === 1);
  const nodosPortada = primerH1
    ? nodos.slice(0, nodos.indexOf(primerH1))
    : nodos;

  const parrafos = nodosPortada
    .filter((n) => n.tagName && n.tagName.toLowerCase() === "p")
    .map((p) => (p.textContent || "").trim())
    .filter(Boolean);

  const indiceFecha = parrafos.findIndex((texto) => REGEX_FECHA.test(texto));
  if (indiceFecha > 0) {
    return parrafos[indiceFecha - 1];
  }
  return "";
};

const extraerSeccionesDesdeHtml = (html) => {
  const documento = new DOMParser().parseFromString(html, "text/html");
  const nodos = Array.from(documento.body.childNodes).filter((n) => n.nodeType === 1);

  const introPorSeccion = {};
  const filasPorSeccion = {};
  CLAVES_CON_TABLA.forEach((clave) => {
    introPorSeccion[clave] = [];
    filasPorSeccion[clave] = [];
  });
  const objetivoItems = [];

  let seccionActual = null;
  let tablaEncontrada = false;

  nodos.forEach((nodo) => {
    const etiqueta = nodo.tagName ? nodo.tagName.toLowerCase() : "";

    if (etiqueta === "h1") {
      const encontrada = buscarSeccion((nodo.textContent || "").trim());
      seccionActual = encontrada ? encontrada.key : null;
      tablaEncontrada = false;
      return;
    }

    if (!seccionActual) return;

    if (seccionActual === "objetivo") {
      if (etiqueta === "ul" || etiqueta === "ol") {
        procesarListaRecursiva(nodo, 0, objetivoItems);
      }
      return;
    }

    if (etiqueta === "table") {
      if (!tablaEncontrada) {
        const config = SECCIONES_SITE_SURVEY.find((s) => s.key === seccionActual);
        filasPorSeccion[seccionActual] = extraerFilasTabla(nodo, config.columnas);
        tablaEncontrada = true;
      }
      return;
    }

    if (!tablaEncontrada) {
      const texto = (nodo.textContent || "").trim();
      if (texto) introPorSeccion[seccionActual].push(texto);
    }
  });

  const construirEncabezadoMasFilas = (clave, prefijo) => {
    const [encabezado, ...resto] = filasPorSeccion[clave];
    const config = SECCIONES_SITE_SURVEY.find((s) => s.key === clave);
    const encabezadoObj = {};
    (encabezado || []).forEach((valor, i) => {
      encabezadoObj[`${prefijo}${i + 1}`] = valor;
    });
    for (let i = (encabezado || []).length; i < config.columnas; i += 1) {
      encabezadoObj[`${prefijo}${i + 1}`] = "";
    }
    return { encabezadoObj, filas: resto.map(filaAObjetoColumnas) };
  };

  const equipo = construirEncabezadoMasFilas("equipo", "equipoEncabezado");
  const cronograma = construirEncabezadoMasFilas("cronograma", "cronogramaEncabezado");

  return {
    nombreProyecto: extraerNombreProyecto(documento),
    referenciaFilas: filasPorSeccion.referencia.map(filaAObjetoColumnas),
    objetivoItems,
    bomIntro: introPorSeccion.bom[0] || "",
    bomFilas: filasPorSeccion.bom.map(filaAObjetoColumnas),
    equipoIntro: introPorSeccion.equipo[0] || "",
    ...equipo.encabezadoObj,
    equipoFilas: equipo.filas,
    cronogramaIntro: introPorSeccion.cronograma[0] || "",
    ...cronograma.encabezadoObj,
    cronogramaFilas: cronograma.filas,
    entregaIntro: introPorSeccion.entrega[0] || "",
    entregaContenido: filasPorSeccion.entrega.map((fila) => fila[0]).join("\n"),
  };
};

export const extraerDatosSiteSurvey = async (archivo) => {
  const arrayBuffer = await archivo.arrayBuffer();
  const logos = [];

  const { value: html } = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      convertImage: mammoth.images.imgElement((imagen) =>
        imagen.read("base64").then((base64) => {
          logos.push({ base64, contentType: imagen.contentType });
          return { src: "" };
        })
      ),
    }
  );

  const secciones = extraerSeccionesDesdeHtml(html);
  const logosTop = logos.slice(0, MAX_LOGOS);
  const logoCliente = logosTop.length > 1 ? logosTop[1] : logosTop[0] || null;

  return { ...secciones, logoCliente };
};
